import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { dlpSettingsApiArea } from '@/services/api/dlpSettingsApiArea';
import { DlpProfile } from '@/services/api/dlpSettingsApiArea/types';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

export interface DplReturnParams {
  itemControl: ItemDlpTypes;
  dlps: DlpProfile[];
  isLoading: boolean;
  itemToEditId: number | null;
  profileToEdit: DlpProfile | undefined;

  setItemControl: Dispatch<SetStateAction<ItemDlpTypes>>;
  setItemToEditId: Dispatch<SetStateAction<number | null>>;
  loadProfiles(): Promise<void>;
  onProfileSaved(profile: DlpProfile): Promise<void>;
  toggleEnabled(id: number, enabled: boolean): Promise<void>;
}

export enum ItemDlpTypes {
  NONE = 0,
  CREATE = 1,
  EDIT = 2
}

const useDlpPage = (): DplReturnParams => {
  const { t } = useTranslation();
  const [dlps, setDlps] = useState<DlpProfile[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [itemToEditId, setItemToEditId] = useState<number | null>(null);
  const [itemControl, setItemControl] = useState<ItemDlpTypes>(ItemDlpTypes.NONE);

  const loadProfiles = useCallback(async () => {
    try {
      setLoading(true);
      const profiles = await dlpSettingsApiArea().GetDlpProfiles();
      console.log('Loaded DLP profiles:', profiles);
      setDlps(profiles);
    } catch (error) {
      console.error('Failed to load DLP profiles:', error);
      notifications.show({
        title: t('dlp.loadError'),
        message: t('dlp.loadErrorMessage'),
        color: 'red'
      });
    } finally {
      setLoading(false);
    }
  }, [t]);

  const onProfileSaved = useCallback(async () => {
    await loadProfiles();
  }, [loadProfiles]);

  const toggleEnabled = useCallback(
    async (id: number, enabled: boolean) => {
      try {
        await dlpSettingsApiArea().ToggleEnabled(id, enabled);
        notifications.show({
          title: t('dlp.success'),
          message: t('dlp.toggleSuccess', {
            action: enabled ? t('dlp.enabled') : t('dlp.disabled')
          }),
          color: 'green'
        });

        await loadProfiles();
      } catch (error) {
        console.error('Failed to toggle DLP profile:', error);
        notifications.show({
          title: t('dlp.error'),
          message: error instanceof Error ? error.message : t('dlp.toggleError'),
          color: 'red'
        });
      }
    },
    [t, loadProfiles]
  );

  useEffect(() => {
    loadProfiles();
  }, []);

  const profileToEdit = dlps.find((p) => p.Id === itemToEditId);

  return {
    itemControl,
    itemToEditId,
    dlps,
    isLoading,
    profileToEdit,
    setItemControl,
    setItemToEditId,
    loadProfiles,
    onProfileSaved,
    toggleEnabled
  };
};

export default useDlpPage;
